import type { TokenView } from './types'

/**
 * 로그인·갱신 응답 **바디**에서 토큰 4종을 꺼낸다 (설계 §2-1(a), D1).
 *
 * 구장 관리자 인증 EP 는 토큰을 **바디로 주고 헤더에도 미러**한다(#583 D5). 바디가 정본이므로
 * 여기를 먼저 보고, 실패하면 `readTokenHeaders()` 로 폴백한다.
 *
 * 🚨 **형식이 서로 다르다.** 바디의 만료 2종은 **ISO-8601**(`"2026-08-02T06:12:44.120Z"`),
 * 헤더는 **epoch millis 문자열**(`"1754038364120"`)이다. `tokenStore.parseEpochMillis` 는 ISO 를
 * 의도적으로 거부하므로(T2 회귀 방지 — `Date.parse("1754…")` = NaN 사고), 변환은 **이 파일 한 곳**에
 * 가두고 `saveTokens` 에는 언제나 epoch millis 문자열만 넘긴다(불변식 3).
 */

/** ISO-8601 또는 epoch millis 문자열 → epoch millis 문자열. 판독 불가면 `null`. */
export function toEpochMillisString(raw: unknown): string | null {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw > 0 ? String(raw) : null
  }
  if (typeof raw !== 'string') return null

  const trimmed = raw.trim()
  if (trimmed.length === 0) return null

  // 이미 epoch millis 문자열이면 그대로 통과시킨다(헤더와 같은 형식으로 내려올 경우 대비).
  if (/^\d+$/.test(trimmed)) return Number(trimmed) > 0 ? trimmed : null

  const parsed = Date.parse(trimmed)
  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : null
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

/**
 * @returns 4종을 모두 읽으면 `TokenView`(만료는 epoch millis 문자열), 하나라도 없으면 `null`.
 *          `null` 은 호출부가 **헤더 폴백**을 시도하고, 그마저 실패하면 로그인 실패로 처리한다.
 */
export function readTokenBody(body: unknown): TokenView | null {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return null

  const record = body as Record<string, unknown>
  const accessToken = readString(record, 'accessToken')
  const refreshToken = readString(record, 'refreshToken')
  const accessTokenExpiresAt = toEpochMillisString(record.accessTokenExpiresAt)
  const refreshTokenExpiresAt = toEpochMillisString(record.refreshTokenExpiresAt)

  if (!accessToken || !refreshToken || !accessTokenExpiresAt || !refreshTokenExpiresAt) return null

  return { accessToken, refreshToken, accessTokenExpiresAt, refreshTokenExpiresAt }
}
